/** @jsxRuntime classic */
/** @jsx jsx */

import React, { Ref } from 'react';
import NextLink from 'next/link';
import { labelStyles } from '@zapier/style-encapsulation';
import {
  AnchorTarget,
  Link as LinkZDS,
  LinkProps,
} from '@zapier/design-system';
import { urlParser } from './linkStrategy';
import { BASE_PATH } from '../../constants/paths';
import { setHttpRefererFromStorage } from 'tracking/TrackingProvider';
import { css, jsx } from '@emotion/react';

type Props = {
  // The path inside pages directory. This is the only required prop.
  href: string;
  color?: LinkProps['color'];
  testId?: string;
  ref?: Ref<HTMLAnchorElement>;
  target?: AnchorTarget;
  referrerPolicy?: LinkProps['referrerPolicy'];
} & React.DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>;

const Styles = labelStyles('Nav', {
  link: css`
    text-decoration: none;
  `,
});

export const Link = ({
  color,
  href,
  children,
  onClick = () => {},
  testId,
  rel,
  ...otherProps
}: Props) => {
  const parsedUrl = urlParser(href);
  const { href: parsedHref, target } = parsedUrl;
  const linkRel = rel || parsedUrl.rel;
  // urlParser reliably outputs absolute blog URLs (converts fully qualified blog -> absolute blog)
  const isBlogLink = parsedHref.startsWith(BASE_PATH);
  const referrerPolicy = parsedUrl.referrerPolicy;

  const aProps = {
    target,
    onClick,
    'data-testid': testId,
    ...otherProps,
  } as const;

  function handleClick(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    setHttpRefererFromStorage();
    onClick(e);
  }

  if (isBlogLink) {
    const nextLinkHref =
      parsedHref === BASE_PATH ? '/' : parsedHref.replace(BASE_PATH, '');
    return (
      <NextLink href={nextLinkHref} passHref>
        {color ? (
          <LinkZDS
            {...aProps}
            color={color}
            component="a"
            onClick={handleClick}
            rel={linkRel}
          >
            {children}
          </LinkZDS>
        ) : (
          <LinkZDS
            {...aProps}
            color={null}
            css={Styles.link}
            component="a"
            onClick={handleClick}
            rel={linkRel}
          >
            {children}
          </LinkZDS>
        )}
      </NextLink>
    );
  }
  return color ? (
    <LinkZDS
      {...aProps}
      color={color}
      href={href}
      component="a"
      onClick={handleClick}
      rel={linkRel}
      referrerPolicy={referrerPolicy}
    >
      {children}
    </LinkZDS>
  ) : (
    <LinkZDS
      {...aProps}
      color={null}
      href={href}
      css={Styles.link}
      component="a"
      onClick={handleClick}
      rel={linkRel}
      referrerPolicy={referrerPolicy}
    >
      {children}
    </LinkZDS>
  );
};
